import { useSelector } from 'react-redux';
import { keyBy } from 'lodash-es';
import {
  FEDERATED_APP_SETTINGS,
  loadRemoteApp,
  resolveRemoteUrl
} from '@linearb/linui-utils';
import { GITSTREAM_METRICS } from '@linearb/linjs-common';
import { useMemo, Suspense, lazy } from 'react';
import { ErrorBoundary } from '@linearb/linui-common';
import { CenterLoader } from 'connected-components/Loader/Loader';
import useWorkingDaysSettings from 'views/root/views/home/hooks/useWorkingDaysSettings';
import type { MetricsInspectionProps } from './types/MetricsInspection';
import { contributorsSelector } from 'state/selectors/contributors';
import { repositoriesSelector } from 'state/selectors/repositories';
import { parseFiltersToMetricsConfig } from './utils';
import { useCopyLink } from 'components/hooks/useCopyLink';
import { useViewInActivity } from 'components/hooks/useViewInActivity';
import useBenchmarkOrgSizeConfig from 'views/root/views/home/hooks/useBenchmarkOrgSizeConfig';

const app = FEDERATED_APP_SETTINGS.METRICS_INSPECTION;

const url = resolveRemoteUrl(process.env.NODE_ENV, window.location, app, true);

const MetricsInspectionApp = lazy(loadRemoteApp(url, app));

interface MetricsInspectionMFAProps extends MetricsInspectionProps {
  metricName: string;
  graph?: {};
}

export function MetricsInspectionMFA({
  dateRange,
  filters,
  isPublic,
  metricsCalculationStrategy,
  metricName,
  graph,
  onClose
}: MetricsInspectionMFAProps) {
  const contributors = useSelector(contributorsSelector);
  const repositories = useSelector(repositoriesSelector);
  const { benchmarkOrgSize } = useBenchmarkOrgSizeConfig();

  const workingDaysSetting = useWorkingDaysSettings({
    teams: filters.team ? [filters.team] : []
  });

  const contributorsById = useMemo(
    () => keyBy(contributors, 'id'),
    [contributors]
  );

  const repositoriesById = useMemo(
    () => keyBy(repositories, 'id'),
    [repositories]
  );

  const metricsConfig = parseFiltersToMetricsConfig({
    metricName,
    filters,
    metricsCalculationStrategy,
    graph
  });

  const { onClickCopyLink } = useCopyLink();
  const { onClickViewInActivity } = useViewInActivity();

  return (
    <ErrorBoundary>
      <Suspense fallback={<CenterLoader />}>
        <MetricsInspectionApp
          dateRange={dateRange}
          filters={filters}
          isPublic={isPublic}
          workingDaysSetting={workingDaysSetting}
          contributorsById={contributorsById}
          repositoriesById={repositoriesById}
          metricsCalculationStrategy={metricsCalculationStrategy}
          metricsConfig={metricsConfig}
          graph={graph}
          onClose={onClose}
          benchmarkOrgSize={benchmarkOrgSize}
          hideScatter={GITSTREAM_METRICS.includes(metricName)}
          {...(!GITSTREAM_METRICS.includes(metricName) && {
            onClickCopyLink,
            onClickViewInActivity
          })}
        />
      </Suspense>
    </ErrorBoundary>
  );
}
